/**
 * PageHeader Component
 *
 * Displays a page header with optional back navigation, title, subtitle, and actions.
 * Provides consistent page header layout across the application.
 *
 * @module components/common/PageHeader
 */

import { memo } from 'react';
import { BackNavigation } from '../BackNavigation';
import type { PageHeaderProps, PageHeaderSize } from './PageHeader.types';
import styles from './PageHeader.module.css';

/**
 * Maps size variant to CSS class suffix
 */
const SIZE_CLASS_SUFFIX: Record<PageHeaderSize, 'titleSm' | 'titleMd' | 'titleLg'> = {
  sm: 'titleSm',
  md: 'titleMd',
  lg: 'titleLg',
};

/**
 * PageHeader Component
 *
 * A responsive page header with back link, title, subtitle, and actions area.
 * Supports three size variants (sm, md, lg) and automatically stacks on mobile.
 *
 * @example
 * ```tsx
 * // Basic usage with title only
 * <PageHeader title="Dashboard" />
 *
 * // With back navigation and subtitle
 * <PageHeader
 *   backLink={{ label: "Back to Dashboard", href: "/dashboard" }}
 *   title="Task Details"
 *   subtitle="View and manage task information"
 * />
 *
 * // With actions
 * <PageHeader
 *   title="Workspace Settings"
 *   subtitle="Configure your workspace"
 *   actions={
 *     <>
 *       <Button variant="outline">Cancel</Button>
 *       <Button>Save Changes</Button>
 *     </>
 *   }
 * />
 *
 * // With editable title (ReactNode)
 * <PageHeader
 *   title={<EditableTitle value={title} onChange={setTitle} />}
 *   size="lg"
 * />
 * ```
 */
export const PageHeader = memo(function PageHeader({
  backLink,
  title,
  subtitle,
  actions,
  size = 'md',
  className,
}: PageHeaderProps) {
  const containerClass = [styles.container, className].filter(Boolean).join(' ');
  const sizeClassName = styles[SIZE_CLASS_SUFFIX[size]];
  const titleClass = [styles.title, sizeClassName].filter(Boolean).join(' ');

  return (
    <header className={containerClass}>
      {backLink && (
        <div className={styles.backLinkRow}>
          <BackNavigation label={backLink.label} href={backLink.href} />
        </div>
      )}

      <div className={styles.titleRow}>
        <div className={styles.titleContent}>
          {typeof title === 'string' ? (
            <h1 className={titleClass}>{title}</h1>
          ) : (
            <div className={titleClass}>{title}</div>
          )}

          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
    </header>
  );
});

PageHeader.displayName = 'PageHeader';
